import { FC, useCallback, useEffect, useRef } from 'react';
import cn from 'classnames';
import { Progress, Upload, UploadProps } from 'antd';

import { useFileUpload, useUploadTranslations } from './hooks';
import { DropzoneArea, InlineControls, PaymentNotices } from './components';
import type { FileUploaderProps } from './types';
import styles from './FileUploader.module.scss';

const noop = () => undefined;

export const FileUploader: FC<FileUploaderProps> = ({
    onSuccess,
    onRemove,
    accept,
    disabled,
    onlyZip,
    onValidationError,
    variant = 'inline',
    className,
    dropzoneTitle,
    dropzoneSubtitle,
    autoStart,
    existingFiles,
    onFilesChange,
    onLocalFilesChange,
    resetSignal,
    message,
}) => {
    const isDropzone = variant === 'dropzone';
    const resolvedAutoStart = autoStart ?? isDropzone;
    const handleExternalRemove = onRemove ?? noop;

    const translations = useUploadTranslations();
    const dropzoneRef = useRef<HTMLDivElement>(null);

    const { state, actions, fileList, hasUpload } = useFileUpload({
        onSuccess,
        onValidationError,
        onLocalFilesChange,
        onFilesChange,
        autoStart: resolvedAutoStart,
        onlyZip,
        existingFiles,
        translations,
    });

    const { visualState, progress, localError } = state;
    const {
        handleFileSelect,
        handleStart,
        handleReset,
        triggerRetry,
        validateFile,
    } = actions;

    useEffect(() => {
        if (resetSignal === null || resetSignal === undefined) return;
        handleReset();
    }, [resetSignal, handleReset]);

    const handleRetry = useCallback(() => {
        triggerRetry(dropzoneRef);
    }, [triggerRetry]);

    const uploadProps: UploadProps = {
        onChange: handleFileSelect as (info: any) => void,
        beforeUpload: file => {
            const isValid = validateFile(file);
            return isValid ? false : Upload.LIST_IGNORE;
        },
        multiple: false,
        fileList: fileList as any,
        onRemove: () => {
            handleExternalRemove();
            handleReset();
        },
        accept,
        showUploadList: !isDropzone,
    };

    const shouldShowInlineProgress = !isDropzone && visualState === 'uploading';
    const hasError = visualState === 'error';

    return (
        <div
            className={cn(
                styles.wrapper,
                isDropzone ? styles.dropzone : styles.inline,
                className
            )}
        >
            <div className={styles.controlRow}>
                {isDropzone ? (
                    <DropzoneArea
                        uploadProps={uploadProps}
                        state={state}
                        disabled={disabled}
                        dropzoneRef={dropzoneRef}
                        dropzoneTitle={dropzoneTitle}
                        dropzoneSubtitle={dropzoneSubtitle}
                        translations={translations}
                        onRetry={handleRetry}
                    />
                ) : (
                    <InlineControls
                        uploadProps={uploadProps}
                        disabled={disabled}
                        hasUpload={hasUpload}
                        translations={translations}
                        onStart={handleStart}
                    />
                )}
            </div>

            {shouldShowInlineProgress && (
                <div className={styles.progressRow}>
                    <Progress
                        percent={progress}
                        status={hasError ? 'exception' : undefined}
                    />
                </div>
            )}

            {localError && !isDropzone && (
                <div className={styles.inlineError}>{localError}</div>
            )}

            <PaymentNotices messages={message ?? []} />
        </div>
    );
};

export default FileUploader;
